<!-- src/pages/ContactPage.vue -->
<template>
    <main class="contact-page" aria-label="Contact M.C Bridge of Hope">

        <!-- HERO -->
        <section class="contact-hero" aria-labelledby="contact-title">
            <div class="contact-hero__glow contact-hero__glow--one" aria-hidden="true"></div>

            <div class="contact-hero__glow contact-hero__glow--two" aria-hidden="true"></div>

            <div class="contact-page__shell contact-hero__shell">
                <div class="contact-hero__content">
                    <p class="contact-hero__eyebrow">
                        M.C Bridge of Hope
                    </p>

                    <h1 id="contact-title" class="contact-hero__title">
                        Let’s start a conversation.
                    </h1>

                    <p class="contact-hero__text">
                        Reach out for support, partnerships,
                        volunteering, donations, or general enquiries.
                        We would love to hear from you.
                    </p>

                    <div class="contact-hero__actions">
                        <a class="contact-btn contact-btn--primary" href="#contact-form">
                            Send a message
                        </a>

                    </div>
                </div>

                <!-- CONTACT CARD -->
                <aside class="contact-card" aria-label="Contact summary">
                    <div class="contact-card__brand">
                        <img :src="logoSrc" alt="M.C Bridge of Hope logo" />
                    </div>

                    <p class="contact-card__label">
                        Contact details
                    </p>

                    <div class="contact-card__list">
                        <a class="contact-card__item">
                            <span>Email</span>
                            <strong>{{ email }}</strong>
                        </a>

                        <div class="contact-card__item">
                            <span>Location</span>
                            <strong>{{ location }}</strong>
                        </div>
                    </div>

                    <!-- SOCIAL LINKS -->
                    <div class="contact-socials">
                        <p class="contact-socials__title">
                            Follow us
                        </p>

                        <div class="contact-socials__grid">
                            <a v-for="social in socialLinks" :key="social.key" :href="social.href"
                                class="contact-social" target="_blank" rel="noopener noreferrer"
                                :aria-label="social.label">
                                <span class="contact-social__icon" aria-hidden="true" v-html="social.svg"></span>

                                <span>
                                    {{ social.label }}
                                </span>
                            </a>
                        </div>
                    </div>
                </aside>
            </div>
        </section>


        <!-- CONTACT FORM -->
        <section class="contact-section" aria-label="Contact form section">
            <div class="contact-page__shell">
                <div class="contact-section__header">
                    <p class="section-eyebrow">
                        Start here
                    </p>

                    <h2>
                        How can we help?
                    </h2>

                    <p>
                        Select the enquiry type and send your message.
                        Your request will be sent directly to the
                        M.C Bridge of Hope team.
                    </p>
                </div>

                <div class="contact-layout">

                    <!-- LEFT -->
                    <aside class="contact-side">
                        <div class="side-panel">
                            <h3>
                                Choose an enquiry type
                            </h3>

                            <p>
                                This helps us direct your message
                                to the right person.
                            </p>

                            <div class="subject-list">
                                <button v-for="item in contactPoints" :key="item" type="button" class="subject-pill"
                                    :class="{
                                        active:
                                            form.subject === item,
                                    }" @click="setSubject(item)">
                                    <span>
                                        {{ item.charAt(0) }}
                                    </span>

                                    {{ item }}
                                </button>
                            </div>
                        </div>

                        <div class="side-panel side-panel--dark">
                            <p class="side-panel__mini">
                                Response
                            </p>

                            <h3>
                                We’ll get back to you
                            </h3>

                            <p>
                                Please include enough detail so
                                the team can understand your
                                enquiry clearly.
                            </p>
                        </div>
                    </aside>


                    <!-- FORM -->
                    <section id="contact-form" class="form-panel" aria-label="Send a message">
                        <div class="form-panel__top">
                            <p class="section-eyebrow">
                                Message form
                            </p>

                            <h2>
                                Send us a message
                            </h2>

                            <p>
                                Fill in your details below and
                                we’ll receive your enquiry by email.
                            </p>
                        </div>

                        <form class="contact-form" @submit.prevent="submitForm">
                            <div class="contact-form__grid">
                                <label class="contact-field">
                                    <span>Name</span>

                                    <input v-model.trim="form.name" type="text" placeholder="Your name"
                                        autocomplete="name" required @input="clearSubmitted" />
                                </label>

                                <label class="contact-field">
                                    <span>Email</span>

                                    <input v-model.trim="form.email" type="email" placeholder="you@example.com"
                                        autocomplete="email" required @input="clearSubmitted" />
                                </label>
                            </div>

                            <label class="contact-field">
                                <span>Subject</span>

                                <select v-model="form.subject" required @change="clearSubmitted">
                                    <option value="General enquiry">
                                        General enquiry
                                    </option>

                                    <option value="Volunteer">
                                        Volunteer
                                    </option>

                                    <option value="Partnership">
                                        Partnership
                                    </option>

                                    <option value="Donation">
                                        Donation
                                    </option>

                                    <option value="Program support">
                                        Program support
                                    </option>
                                </select>
                            </label>

                            <label class="contact-field">
                                <span>Message</span>

                                <textarea v-model.trim="form.message" rows="7" placeholder="How can we help?" required
                                    @input="clearSubmitted"></textarea>
                            </label>

                            <div class="contact-form__actions">
                                <button class="contact-btn contact-btn--submit" type="submit" :disabled="submitting">
                                    {{
                                        submitting
                                            ? "Sending..."
                                            : "Send message"
                                    }}
                                </button>

                                <p v-if="submitted" class="
                                        contact-alert
                                        contact-alert--success
                                    " role="status">
                                    Thanks. Your message has been
                                    sent successfully.
                                </p>

                                <p v-if="errorMessage" class="
                                        contact-alert
                                        contact-alert--error
                                    " role="alert">
                                    {{ errorMessage }}
                                </p>
                            </div>
                        </form>
                    </section>
                </div>
            </div>
        </section>


        <!-- OTHER WAYS -->
        <section class="connect-section" aria-label="Other ways to connect">
            <div class="contact-page__shell">
                <div class="connect-section__header">
                    <p class="section-eyebrow">
                        Other ways to connect
                    </p>

                    <h2>
                        Support the mission
                    </h2>
                </div>

                <div class="connect-grid">
                    <RouterLink class="connect-card" to="/programs">
                        <span>01</span>

                        <strong>
                            Explore programs
                        </strong>

                        <p>
                            Learn about the outreach and support
                            areas we are building.
                        </p>
                    </RouterLink>

                    <RouterLink class="connect-card" to="/donate">
                        <span>02</span>

                        <strong>
                            Donate
                        </strong>

                        <p>
                            Support practical care, community
                            outreach, and future programs.
                        </p>
                    </RouterLink>

                    <RouterLink class="connect-card" to="/about">
                        <span>03</span>

                        <strong>
                            About us
                        </strong>

                        <p>
                            Understand our mission, values,
                            and the purpose behind our work.
                        </p>
                    </RouterLink>
                </div>
            </div>
        </section>
    </main>
</template>


<script>
import { useHead } from "@unhead/vue";
import logoSrc from "@/assets/logo7.png";


const ICONS = {
    instagram: `
        <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M7.5 2.75h9A4.75 4.75 0 0 1 21.25 7.5v9A4.75 4.75 0 0 1 16.5 21.25h-9A4.75 4.75 0 0 1 2.75 16.5v-9A4.75 4.75 0 0 1 7.5 2.75Z"
                stroke="currentColor"
                stroke-width="1.7"
            />

            <path
                d="M12 16.25A4.25 4.25 0 1 0 12 7.75a4.25 4.25 0 0 0 0 8.5Z"
                stroke="currentColor"
                stroke-width="1.7"
            />

            <path
                d="M17.2 6.9h.01"
                stroke="currentColor"
                stroke-width="3"
                stroke-linecap="round"
            />
        </svg>
    `,

    facebook: `
        <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M13.5 21v-7h2.4l.4-3h-2.8V9.1c0-.9.3-1.6 1.7-1.6H16.4V4.8c-.6-.1-1.6-.2-2.7-.2-2.6 0-4.2 1.6-4.2 4.5V11H7.2v3h2.3v7h4Z"
            />
        </svg>
    `,

    linkedin: `
        <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M6.5 8.25H3.25V21H6.5V8.25ZM4.88 3A1.88 1.88 0 1 0 4.88 6.75 1.88 1.88 0 0 0 4.88 3ZM21 13.7C21 9.85 18.94 8.06 16.2 8.06c-2.21 0-3.2 1.22-3.76 2.07V8.25H9.2V21h3.24v-6.32c0-1.67.32-3.29 2.39-3.29 2.04 0 2.06 1.91 2.06 3.4V21H21v-7.3Z"
            />
        </svg>
    `,

    tiktok: `
        <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M14.5 3h3.1c.25 1.45 1.06 2.67 2.4 3.45V9.5a8.14 8.14 0 0 1-2.72-.72v6.08A6.14 6.14 0 1 1 12 8.78v3.12a3.06 3.06 0 1 0 2.5 3V3Z"
            />
        </svg>
    `,
};


const SOCIAL_LINKS = [
    {
        key: "instagram",
        label: "Instagram",
        href: "https://www.instagram.com/bridgeofhope.mc/",
        svg: ICONS.instagram,
    },

    {
        key: "facebook",
        label: "Facebook",
        href:
            "https://www.facebook.com/profile.php?id=61594445814032",
        svg: ICONS.facebook,
    },

    {
        key: "linkedin",
        label: "LinkedIn",
        href:
            "https://www.linkedin.com/in/mc-bridge-of-hope-44aa23434/",
        svg: ICONS.linkedin,
    },

    {
        key: "tiktok",
        label: "TikTok",
        href:
            "https://www.tiktok.com/@marychan.org?_r=1&_t=ZS-99dXxN98DoL",
        svg: ICONS.tiktok,
    },
];


export default {
    name: "ContactPage",

    setup() {
        useHead({
            title:
                "Contact M.C Bridge of Hope | Support, Donations & Partnerships",

            meta: [
                {
                    name: "description",
                    content:
                        "Contact M.C Bridge of Hope for support, partnerships, volunteering, donations, program enquiries, or general questions.",
                },

                {
                    name: "author",
                    content: "M.C Bridge of Hope",
                },

                {
                    name: "robots",
                    content:
                        "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
                },

                {
                    property: "og:title",
                    content:
                        "Contact M.C Bridge of Hope | Support, Donations & Partnerships",
                },

                {
                    property: "og:description",
                    content:
                        "Reach out to M.C Bridge of Hope for support, partnerships, volunteering, donations, program support, or general enquiries.",
                },

                {
                    property: "og:type",
                    content: "website",
                },

                {
                    property: "og:url",
                    content:
                        "https://marychan.org.au/contact",
                },

                {
                    property: "og:image",
                    content:
                        "https://marychan.org.au/logo7.jpeg",
                },

                {
                    property: "og:image:alt",
                    content:
                        "M.C Bridge of Hope contact and community support",
                },

                {
                    property: "og:site_name",
                    content: "M.C Bridge of Hope",
                },

                {
                    property: "og:locale",
                    content: "en_AU",
                },

                {
                    name: "twitter:card",
                    content: "summary_large_image",
                },

                {
                    name: "twitter:title",
                    content:
                        "Contact M.C Bridge of Hope | Support, Donations & Partnerships",
                },

                {
                    name: "twitter:description",
                    content:
                        "Send a message to M.C Bridge of Hope for support, partnerships, donations, volunteering, or program enquiries.",
                },

                {
                    name: "twitter:image",
                    content:
                        "https://marychan.org.au/logo7.jpeg",
                },
            ],

            link: [
                {
                    rel: "canonical",
                    href:
                        "https://marychan.org.au/contact",
                },

                {
                    rel: "alternate",
                    hrefLang: "en-au",
                    href:
                        "https://marychan.org.au/contact",
                },

                {
                    rel: "alternate",
                    hrefLang: "x-default",
                    href:
                        "https://marychan.org.au/contact",
                },
            ],

            script: [
                {
                    type: "application/ld+json",

                    children: JSON.stringify({
                        "@context":
                            "https://schema.org",

                        "@graph": [
                            {
                                "@type":
                                    "Organization",

                                "@id":
                                    "https://marychan.org.au/#organization",

                                name:
                                    "M.C Bridge of Hope",

                                url:
                                    "https://marychan.org.au/",

                                logo:
                                    "https://marychan.org.au/logo7.jpeg",

                                email:
                                    "Connect@marychan.org.au",

                                sameAs: [
                                    "https://www.instagram.com/bridgeofhope.mc/",
                                    "https://www.facebook.com/profile.php?id=61594445814032",
                                    "https://www.linkedin.com/in/mc-bridge-of-hope-44aa23434/",
                                    "https://www.tiktok.com/@marychan.org",
                                ],

                                areaServed: {
                                    "@type":
                                        "Country",

                                    name:
                                        "Australia",
                                },

                                contactPoint: {
                                    "@type":
                                        "ContactPoint",

                                    contactType:
                                        "General enquiries",

                                    email:
                                        "Connect@marychan.org.au",

                                    areaServed:
                                        "AU",

                                    availableLanguage: [
                                        "English",
                                    ],
                                },
                            },

                            {
                                "@type":
                                    "WebSite",

                                "@id":
                                    "https://marychan.org.au/#website",

                                url:
                                    "https://marychan.org.au/",

                                name:
                                    "M.C Bridge of Hope",

                                inLanguage:
                                    "en-AU",

                                publisher: {
                                    "@id":
                                        "https://marychan.org.au/#organization",
                                },
                            },

                            {
                                "@type":
                                    "ContactPage",

                                "@id":
                                    "https://marychan.org.au/contact#webpage",

                                url:
                                    "https://marychan.org.au/contact",

                                name:
                                    "Contact M.C Bridge of Hope | Support, Donations & Partnerships",

                                description:
                                    "Contact M.C Bridge of Hope for support, partnerships, volunteering, donations, program enquiries, or general questions.",

                                isPartOf: {
                                    "@id":
                                        "https://marychan.org.au/#website",
                                },

                                about: {
                                    "@id":
                                        "https://marychan.org.au/#organization",
                                },

                                inLanguage:
                                    "en-AU",
                            },

                            {
                                "@type":
                                    "BreadcrumbList",

                                "@id":
                                    "https://marychan.org.au/contact#breadcrumb",

                                itemListElement: [
                                    {
                                        "@type":
                                            "ListItem",

                                        position: 1,

                                        name:
                                            "Home",

                                        item:
                                            "https://marychan.org.au/",
                                    },

                                    {
                                        "@type":
                                            "ListItem",

                                        position: 2,

                                        name:
                                            "Contact",

                                        item:
                                            "https://marychan.org.au/contact",
                                    },
                                ],
                            },
                        ],
                    }),
                },
            ],
        });

        return {};
    },


    data() {
        return {
            logoSrc,

            email:
                "Connect@marychan.org.au",

            location:
                "Australia",

            socialLinks:
                SOCIAL_LINKS,

            submitted:
                false,

            submitting:
                false,

            errorMessage:
                "",

            contactPoints: [
                "General enquiry",
                "Program support",
                "Donation",
                "Volunteer",
                "Partnership",
            ],

            form: {
                name: "",
                email: "",
                subject:
                    "General enquiry",
                message: "",
            },
        };
    },


    mounted() {
        if (
            typeof window !==
            "undefined"
        ) {
            window.scrollTo(
                0,
                0,
            );
        }
    },


    methods: {
        setSubject(subject) {
            this.form.subject =
                subject;

            this.clearSubmitted();

            if (
                typeof document !==
                "undefined"
            ) {
                const form =
                    document.getElementById(
                        "contact-form",
                    );

                if (form) {
                    form.scrollIntoView({
                        behavior:
                            "smooth",

                        block:
                            "start",
                    });
                }
            }
        },


        clearSubmitted() {
            this.submitted =
                false;

            this.errorMessage =
                "";
        },


        async submitForm() {
            if (
                this.submitting
            ) {
                return;
            }

            this.submitted =
                false;

            this.errorMessage =
                "";

            this.submitting =
                true;

            try {
                const apiBaseUrl =
                    "https://my-app-two-1d26224db3b5.herokuapp.com";

                const response =
                    await fetch(
                        `${apiBaseUrl}/marychan/contact-messages`,
                        {
                            method:
                                "POST",

                            headers: {
                                "Content-Type":
                                    "application/json",
                            },

                            body:
                                JSON.stringify(
                                    {
                                        name:
                                            this
                                                .form
                                                .name,

                                        email:
                                            this
                                                .form
                                                .email,

                                        subject:
                                            this
                                                .form
                                                .subject,

                                        message:
                                            this
                                                .form
                                                .message,
                                    },
                                ),
                        },
                    );

                const result =
                    await response
                        .json()
                        .catch(
                            () =>
                                null,
                        );

                if (
                    !response.ok
                ) {
                    throw new Error(
                        result?.message ||
                        "Unable to send your message. Please try again.",
                    );
                }

                this.submitted =
                    true;

                this.form = {
                    name: "",
                    email: "",
                    subject:
                        "General enquiry",
                    message: "",
                };
            } catch (error) {
                this.errorMessage =
                    error.message ||
                    "Unable to send your message. Please try again.";
            } finally {
                this.submitting =
                    false;
            }
        },
    },
};
</script>


<style scoped lang="scss">
.contact-page {
    --bg: #f7f3ec;
    --cream: #fffaf1;
    --paper: #ffffff;
    --ink: #15202b;
    --muted: rgba(21, 32, 43, 0.68);
    --soft: rgba(21, 32, 43, 0.1);
    --brand: #0b3f57;
    --brand-deep: #072f42;
    --accent: #b9996b;
    --accent-soft: rgba(185, 153, 107, 0.22);
    --success: #0f766e;
    --error: #991b1b;
    --radius-xl: 34px;
    --radius-lg: 24px;
    --radius-md: 16px;
    --shadow: 0 24px 70px rgba(7, 47, 66, 0.16);
    --shadow-soft: 0 14px 36px rgba(7, 47, 66, 0.1);

    min-height: 100%;
    overflow: hidden;
    color: var(--ink);

    background:
        radial-gradient(circle at top left,
            rgba(185, 153, 107, 0.2),
            transparent 34%),
        linear-gradient(180deg,
            var(--bg) 0%,
            #f9fbfc 100%);


    .contact-page__shell {
        width: min(1160px,
                calc(100% - 32px));

        margin: 0 auto;

        @media (min-width: 760px) {
            width: min(1160px,
                    calc(100% - 48px));
        }

        @media (min-width: 1120px) {
            width: min(1180px,
                    calc(100% - 72px));
        }
    }


    .section-eyebrow,
    .contact-hero__eyebrow {
        margin: 0 0 10px;

        color: var(--accent);

        font-size: 12px;

        font-weight: 950;

        letter-spacing: 0.16em;

        text-transform: uppercase;
    }


    /* BUTTONS */

    .contact-btn {
        appearance: none;

        display: inline-flex;

        align-items: center;

        justify-content: center;

        min-height: 48px;

        padding: 13px 20px;

        border: 0;

        border-radius: 999px;

        cursor: pointer;

        font-family: inherit;

        font-weight: 950;

        text-decoration: none;

        transition:
            transform 180ms ease,
            box-shadow 180ms ease,
            background 180ms ease,
            opacity 180ms ease;

        &:hover {
            transform:
                translateY(-2px);
        }

        &:disabled {
            cursor:
                not-allowed;

            opacity: 0.7;

            transform: none;
        }
    }


    .contact-btn--primary,
    .contact-btn--submit {
        color: #fff;

        background:
            radial-gradient(circle at top left,
                rgba(255, 255, 255, 0.24),
                transparent 32%),
            linear-gradient(135deg,
                var(--brand),
                var(--brand-deep));

        box-shadow:
            0 16px 34px rgba(7, 47, 66, 0.25);
    }


    .contact-btn--light {
        color: var(--brand);

        background:
            rgba(255,
                255,
                255,
                0.8);

        box-shadow:
            0 12px 28px rgba(7, 47, 66, 0.1);
    }


    /* HERO */

    .contact-hero {
        position: relative;

        isolation: isolate;

        padding:
            82px 0 72px;

        background:
            linear-gradient(135deg,
                rgba(11, 63, 87, 0.98),
                rgba(7, 47, 66, 0.94)),
            radial-gradient(circle at 10% 10%,
                rgba(185, 153, 107, 0.34),
                transparent 32%);

        @media (max-width: 720px) {
            padding:
                58px 0 46px;
        }
    }


    .contact-hero__glow {
        position: absolute;

        z-index: -1;

        border-radius:
            999px;

        filter:
            blur(2px);

        opacity:
            0.74;

        pointer-events:
            none;
    }


    .contact-hero__glow--one {
        top: -90px;

        right: 10%;

        width: 260px;

        height: 260px;

        background:
            rgba(185,
                153,
                107,
                0.26);
    }


    .contact-hero__glow--two {
        bottom: -130px;

        left: 6%;

        width: 320px;

        height: 320px;

        background:
            rgba(255,
                250,
                241,
                0.12);
    }


    .contact-hero__shell {
        display: grid;

        gap: 28px;

        align-items:
            center;

        @media (min-width: 920px) {
            grid-template-columns:
                minmax(0, 1fr) 390px;
        }
    }


    .contact-hero__content {
        max-width: 760px;
    }


    .contact-hero__title {
        margin: 0;

        max-width: 800px;

        color: #fff;

        font-size:
            clamp(44px,
                7vw,
                86px);

        line-height:
            0.95;

        font-weight:
            1000;

        letter-spacing:
            -2px;
    }


    .contact-hero__text {
        margin:
            22px 0 0;

        max-width:
            650px;

        color:
            rgba(255,
                255,
                255,
                0.78);

        font-size:
            17px;

        line-height:
            1.85;
    }


    .contact-hero__actions {
        display: flex;

        flex-wrap: wrap;

        gap: 12px;

        margin-top:
            28px;
    }


    /* CONTACT CARD */

    .contact-card {
        position: relative;

        overflow: hidden;

        padding: 24px;

        border:
            1px solid rgba(255,
                255,
                255,
                0.22);

        border-radius:
            var(--radius-xl);

        background:
            rgba(255,
                255,
                255,
                0.12);

        box-shadow:
            var(--shadow);

        backdrop-filter:
            blur(18px);

        &::before {
            content: "";

            position:
                absolute;

            inset: 0;

            background:
                radial-gradient(circle at top right,
                    rgba(185,
                        153,
                        107,
                        0.3),
                    transparent 42%),
                linear-gradient(180deg,
                    rgba(255,
                        255,
                        255,
                        0.16),
                    rgba(255,
                        255,
                        255,
                        0.07));

            pointer-events:
                none;
        }
    }


    .contact-card__brand,
    .contact-card__label,
    .contact-card__list,
    .contact-socials {
        position: relative;

        z-index: 1;
    }


    .contact-card__brand {
        display: grid;

        width: 82px;

        height: 82px;

        place-items:
            center;

        margin-bottom:
            22px;

        border-radius:
            28px;

        background:
            rgba(255,
                255,
                255,
                0.92);

        img {
            width: 64px;

            height: 64px;

            object-fit:
                contain;
        }
    }


    .contact-card__label {
        margin:
            0 0 14px;

        color:
            rgba(255,
                255,
                255,
                0.68);

        font-size:
            12px;

        font-weight:
            950;

        letter-spacing:
            0.14em;

        text-transform:
            uppercase;
    }


    .contact-card__list {
        display: grid;

        gap: 10px;
    }


    .contact-card__item {
        display: grid;

        gap: 4px;

        padding:
            14px;

        border:
            1px solid rgba(255,
                255,
                255,
                0.16);

        border-radius:
            18px;

        color: #fff;

        background:
            rgba(255,
                255,
                255,
                0.08);

        text-decoration:
            none;

        transition:
            background 180ms ease,
            transform 180ms ease;

        &:is(a):hover {
            background:
                rgba(255,
                    255,
                    255,
                    0.14);

            transform:
                translateY(-1px);
        }

        span {
            color:
                rgba(255,
                    255,
                    255,
                    0.6);

            font-size:
                11px;

            font-weight:
                900;

            letter-spacing:
                0.1em;

            text-transform:
                uppercase;
        }

        strong {
            color:
                #fff;

            font-size:
                15px;

            word-break:
                break-word;
        }
    }


    /* SOCIAL LINKS */

    .contact-socials {
        margin-top:
            22px;

        padding-top:
            18px;

        border-top:
            1px solid rgba(255,
                255,
                255,
                0.16);
    }


    .contact-socials__title {
        margin:
            0 0 12px;

        color:
            rgba(255,
                255,
                255,
                0.68);

        font-size:
            11px;

        font-weight:
            950;

        letter-spacing:
            0.12em;

        text-transform:
            uppercase;
    }


    .contact-socials__grid {
        display: grid;

        grid-template-columns:
            repeat(2, 1fr);

        gap: 8px;
    }


    .contact-social {
        display: flex;

        align-items: center;

        gap: 9px;

        min-width: 0;

        padding:
            11px 12px;

        border:
            1px solid rgba(255,
                255,
                255,
                0.16);

        border-radius:
            14px;

        color: #fff;

        background:
            rgba(255,
                255,
                255,
                0.08);

        font-size:
            13px;

        font-weight:
            850;

        text-decoration:
            none;

        transition:
            transform 180ms ease,
            background 180ms ease,
            border-color 180ms ease;

        &:hover {
            transform:
                translateY(-2px);

            background:
                rgba(255,
                    255,
                    255,
                    0.15);

            border-color:
                rgba(185,
                    153,
                    107,
                    0.5);
        }
    }


    .contact-social__icon {
        display: grid;

        place-items:
            center;

        flex:
            0 0 auto;

        color:
            var(--accent);
    }


    .contact-social__icon :deep(svg) {
        display: block;
    }


    /* CONTACT FORM SECTION */

    .contact-section {
        padding:
            72px 0;

        background:
            radial-gradient(circle at top right,
                rgba(185,
                    153,
                    107,
                    0.14),
                transparent 34%),
            linear-gradient(180deg,
                #f9fbfc 0%,
                var(--cream) 100%);

        @media (max-width: 720px) {
            padding:
                50px 0;
        }
    }


    .contact-section__header {
        max-width:
            720px;

        margin-bottom:
            26px;

        h2 {
            margin: 0;

            color:
                var(--brand);

            font-size:
                clamp(32px,
                    4vw,
                    52px);

            line-height:
                1;

            font-weight:
                1000;

            letter-spacing:
                -1px;
        }

        p {
            margin:
                14px 0 0;

            color:
                var(--muted);

            line-height:
                1.75;
        }
    }


    .contact-layout {
        display: grid;

        gap: 22px;

        align-items:
            start;

        @media (min-width: 980px) {
            grid-template-columns:
                350px minmax(0,
                    1fr);
        }
    }


    .contact-side {
        display: grid;

        gap: 16px;
    }


    .side-panel,
    .form-panel {
        border:
            1px solid rgba(7,
                47,
                66,
                0.1);

        border-radius:
            var(--radius-xl);

        background:
            rgba(255,
                255,
                255,
                0.82);

        box-shadow:
            var(--shadow-soft);
    }


    .side-panel {
        padding:
            22px;

        h3 {
            margin: 0;

            color:
                var(--brand);

            font-size:
                24px;

            line-height:
                1.12;

            font-weight:
                1000;
        }

        p {
            margin:
                10px 0 0;

            color:
                var(--muted);

            line-height:
                1.7;
        }
    }


    .side-panel--dark {
        color: #fff;

        background:
            radial-gradient(circle at top right,
                rgba(185,
                    153,
                    107,
                    0.28),
                transparent 42%),
            linear-gradient(135deg,
                var(--brand),
                var(--brand-deep));

        h3,
        p {
            color: #fff;
        }

        p {
            opacity:
                0.76;
        }
    }


    .side-panel__mini {
        margin:
            0 0 8px !important;

        color:
            var(--accent) !important;

        font-size:
            12px;

        font-weight:
            950;

        letter-spacing:
            0.14em;

        text-transform:
            uppercase;

        opacity:
            1 !important;
    }


    .subject-list {
        display: grid;

        gap: 10px;

        margin-top:
            18px;
    }


    .subject-pill {
        appearance: none;

        display: flex;

        align-items:
            center;

        gap: 10px;

        width: 100%;

        padding:
            13px 14px;

        border:
            1px solid rgba(7,
                47,
                66,
                0.1);

        border-radius:
            999px;

        color:
            var(--ink);

        background:
            #fff;

        cursor:
            pointer;

        font-family:
            inherit;

        font-weight:
            900;

        text-align:
            left;

        transition:
            transform 180ms ease,
            border-color 180ms ease,
            box-shadow 180ms ease,
            background 180ms ease;

        span {
            display: grid;

            width: 28px;

            height: 28px;

            place-items:
                center;

            border-radius:
                999px;

            color: #fff;

            background:
                linear-gradient(135deg,
                    var(--brand),
                    var(--accent));

            font-size:
                12px;

            font-weight:
                950;
        }

        &:hover,
        &.active {
            transform:
                translateX(4px);

            border-color:
                rgba(185,
                    153,
                    107,
                    0.4);

            background:
                #fffaf1;

            box-shadow:
                0 10px 24px rgba(7,
                    47,
                    66,
                    0.09);
        }
    }


    .form-panel {
        padding: 28px;

        background:
            radial-gradient(circle at top right,
                rgba(185,
                    153,
                    107,
                    0.16),
                transparent 34%),
            rgba(255,
                255,
                255,
                0.92);

        @media (max-width: 640px) {
            padding:
                20px;
        }
    }


    .form-panel__top {
        margin-bottom:
            22px;

        h2 {
            margin: 0;

            color:
                var(--brand);

            font-size:
                clamp(26px,
                    3vw,
                    40px);

            line-height:
                1.08;

            font-weight:
                1000;

            letter-spacing:
                -0.6px;
        }

        p {
            margin:
                10px 0 0;

            color:
                var(--muted);

            line-height:
                1.7;
        }
    }


    .contact-form {
        display: grid;

        gap: 16px;
    }


    .contact-form__grid {
        display: grid;

        gap: 16px;

        @media (min-width: 700px) {
            grid-template-columns:
                1fr 1fr;
        }
    }


    .contact-field {
        display: grid;

        gap: 8px;

        span {
            color:
                rgba(21,
                    32,
                    43,
                    0.58);

            font-size:
                12px;

            font-weight:
                950;

            letter-spacing:
                0.1em;

            text-transform:
                uppercase;
        }

        input,
        select,
        textarea {
            width: 100%;

            border:
                1px solid rgba(7,
                    47,
                    66,
                    0.14);

            border-radius:
                18px;

            outline: none;

            box-sizing:
                border-box;

            color:
                var(--ink);

            background:
                #fff;

            font-family:
                inherit;

            font-size:
                15px;

            font-weight:
                750;

            transition:
                border-color 180ms ease,
                box-shadow 180ms ease,
                background 180ms ease;

            &:focus {
                border-color:
                    rgba(185,
                        153,
                        107,
                        0.62);

                box-shadow:
                    0 0 0 5px rgba(185,
                        153,
                        107,
                        0.16);

                background:
                    #fffefb;
            }
        }

        input,
        select {
            height: 54px;

            padding:
                0 14px;
        }

        textarea {
            min-height:
                170px;

            padding:
                14px;

            resize:
                vertical;

            line-height:
                1.7;
        }

        select {
            appearance:
                none;

            background-image:
                linear-gradient(45deg,
                    transparent 50%,
                    rgba(21,
                        32,
                        43,
                        0.62) 50%),
                linear-gradient(135deg,
                    rgba(21,
                        32,
                        43,
                        0.62) 50%,
                    transparent 50%);

            background-position:
                calc(100% - 18px) 24px,
                calc(100% - 13px) 24px;

            background-size:
                5px 5px,
                5px 5px;

            background-repeat:
                no-repeat;
        }
    }


    .contact-form__actions {
        display: grid;

        gap: 12px;

        margin-top:
            4px;
    }


    .contact-btn--submit {
        width: 100%;

        min-height:
            56px;

        font-size:
            15px;
    }


    .contact-alert {
        margin: 0;

        padding:
            13px 15px;

        border-radius:
            18px;

        font-weight:
            850;

        line-height:
            1.6;
    }


    .contact-alert--success {
        color:
            var(--success);

        border:
            1px solid rgba(15,
                118,
                110,
                0.2);

        background:
            rgba(240,
                253,
                250,
                0.94);
    }


    .contact-alert--error {
        color:
            var(--error);

        border:
            1px solid rgba(153,
                27,
                27,
                0.18);

        background:
            rgba(254,
                242,
                242,
                0.94);
    }


    /* CONNECT */

    .connect-section {
        padding:
            66px 0;

        background:
            #fff;

        @media (max-width: 720px) {
            padding:
                48px 0;
        }
    }


    .connect-section__header {
        margin-bottom:
            24px;

        h2 {
            margin: 0;

            color:
                var(--brand);

            font-size:
                clamp(30px,
                    4vw,
                    48px);

            line-height:
                1;

            font-weight:
                1000;

            letter-spacing:
                -0.8px;
        }
    }


    .connect-grid {
        display: grid;

        gap: 16px;

        @media (min-width: 860px) {
            grid-template-columns:
                repeat(3,
                    1fr);
        }
    }


    .connect-card {
        display: grid;

        gap: 12px;

        min-height:
            220px;

        padding:
            24px;

        border:
            1px solid rgba(7,
                47,
                66,
                0.1);

        border-radius:
            var(--radius-xl);

        color:
            var(--ink);

        background:
            radial-gradient(circle at top right,
                rgba(185,
                    153,
                    107,
                    0.12),
                transparent 38%),
            #fffaf1;

        text-decoration:
            none;

        box-shadow:
            var(--shadow-soft);

        transition:
            transform 190ms ease,
            box-shadow 190ms ease,
            border-color 190ms ease;

        span {
            color:
                var(--accent);

            font-size:
                13px;

            font-weight:
                1000;

            letter-spacing:
                0.14em;
        }

        strong {
            color:
                var(--brand);

            font-size:
                24px;

            line-height:
                1.1;

            font-weight:
                1000;
        }

        p {
            margin: 0;

            color:
                var(--muted);

            line-height:
                1.7;
        }

        &:hover {
            transform:
                translateY(-6px);

            border-color:
                rgba(185,
                    153,
                    107,
                    0.38);

            box-shadow:
                0 24px 54px rgba(7,
                    47,
                    66,
                    0.14);
        }
    }


    /* MOBILE */

    @media (max-width: 560px) {

        .contact-hero__actions,
        .contact-btn {
            width: 100%;
        }

        .contact-card {
            padding:
                20px;
        }

        .contact-socials__grid {
            grid-template-columns:
                1fr 1fr;
        }
    }


    @media (prefers-reduced-motion: reduce) {

        *,
        *::before,
        *::after {
            scroll-behavior:
                auto !important;

            animation-duration:
                0.001ms !important;

            animation-iteration-count:
                1 !important;

            transition-duration:
                0.001ms !important;
        }
    }
}
</style>